(() => {
  const configElement = document.getElementById("AstivaAgentAccessConfig");
  if (!configElement || window.astivaAgentAccessInitialized) return;

  window.astivaAgentAccessInitialized = true;

  const config = JSON.parse(configElement.textContent || "{}");
  const enforceAgentRegion = config.enforceAgentRegion === true;
  const dialog = document.getElementById("AstivaAgentAccessDialog");
  const form = document.getElementById("AstivaAgentAccessForm");
  const input = document.getElementById("AstivaAgentNpn");
  const status = document.getElementById("AstivaAgentAccessStatus");
  const submitButton = form?.querySelector('[type="submit"]');
  const verifiedPanel = dialog?.querySelector("[data-astiva-agent-verified]");
  const verifiedMessage = dialog?.querySelector(
    "[data-astiva-agent-verified-message]",
  );
  const clearButton = dialog?.querySelector("[data-astiva-agent-clear]");
  const shopifyLocalizedRoot =
    typeof window.Shopify?.routes?.root === "string"
      ? window.Shopify.routes.root
      : "";
  const rootUrl = new URL(
    shopifyLocalizedRoot || config.rootUrl || "/",
    window.location.origin,
  );
  const catalogUrl = new URL(
    config.catalogUrl || "collections/all",
    window.location.origin,
  );
  const regionalCollectionUrls = {
    "north-ca": String(config.northCollectionUrl || "").trim(),
    "south-ca": String(config.southCollectionUrl || "").trim(),
  };
  const storageKey = config.storageKey || "astiva-agent-npn";
  const cartOwnerKey = config.cartOwnerKey || "astiva-cart-owner-v2";
  const storefrontApiEndpoint = String(config.storefrontApiEndpoint || "").trim();
  const storefrontAccessToken = String(config.storefrontAccessToken || "").trim();
  const storefrontLanguageCode = String(
    config.languageCode || document.documentElement.lang || "en",
  )
    .trim()
    .replace(/-/g, "_")
    .toUpperCase();
  const agentMetaobjectType = String(
    config.agentMetaobjectType || "astiva_agent",
  ).trim();
  const directoryHintKey =
    config.directoryHintKey || `${storageKey}-directory-page-v1`;
  const directoryLookupConcurrency = 4;
  const checkoutLocationParameterNames = {
    country: "checkout[shipping_address][country]",
    province: "checkout[shipping_address][province]",
  };
  const directoryPagePromises = new Map();
  const directLookupPromises = new Map();
  let inlineDirectoryPage;
  let currentAgent;
  let pendingDestination;
  let isSubmitting = false;
  let isPreparingCheckout = false;
  let bypassCheckoutPermalink = false;

  const normalizeNpn = (value) => String(value || "").replace(/\D/g, "");
  const unlimitedQuantityNpn = normalizeNpn(config.unlimitedQuantityNpn);
  const normalizePathname = (value) =>
    String(value || "/").replace(/\/+$/, "") || "/";
  const isCatalogUrl = (destination) =>
    destination.origin === window.location.origin &&
    normalizePathname(destination.pathname) ===
      normalizePathname(catalogUrl.pathname);
  const regionalCollectionUrl = (agent) => {
    const catalogRegion =
      agent?.region === "other" ? "south-ca" : agent?.region;
    const configuredUrl = regionalCollectionUrls[catalogRegion];
    return configuredUrl
      ? new URL(configuredUrl, window.location.origin)
      : null;
  };
  const resolveAgentDestination = (destination, agent) => {
    if (!destination) return "";

    try {
      const requestedUrl = new URL(destination, window.location.href);
      if (!isCatalogUrl(requestedUrl)) return requestedUrl.toString();
      return regionalCollectionUrl(agent)?.toString() || requestedUrl.toString();
    } catch (_) {
      return "";
    }
  };
  const normalizeAgentRegion = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z]/g, "");
    if (
      ["northcalifornia", "northerncalifornia", "northca", "norcal"].includes(
        normalized,
      )
    )
      return "north-ca";
    if (
      ["southcalifornia", "southerncalifornia", "southca", "socal"].includes(
        normalized,
      )
    )
      return "south-ca";
    if (
      ["other", "outsidecalifornia", "noncalifornia", "nonca"].includes(
        normalized,
      )
    )
      return "other";
    return "none";
  };
  const readSessionNpn = () => {
    try {
      return normalizeNpn(window.sessionStorage.getItem(storageKey));
    } catch (_) {
      return "";
    }
  };

  const writeSessionNpn = (npn) => {
    try {
      if (npn) window.sessionStorage.setItem(storageKey, npn);
      else {
        window.sessionStorage.removeItem(storageKey);
        window.sessionStorage.removeItem(directoryHintKey);
      }
    } catch (_) {
      // Access remains valid for this page even when browser storage is unavailable.
    }
  };

  const readDirectoryHint = () => {
    try {
      const value = JSON.parse(
        window.sessionStorage.getItem(directoryHintKey) || "null",
      );
      const page = Number.parseInt(value?.page, 10);
      const pageParameter = String(value?.pageParameter || "");
      if (
        page < 1 ||
        page > 100 ||
        !/^[a-zA-Z0-9_]+$/.test(pageParameter)
      )
        return null;
      return { page, pageParameter };
    } catch (_) {
      return null;
    }
  };

  const writeDirectoryHint = (page, pageParameter) => {
    try {
      window.sessionStorage.setItem(
        directoryHintKey,
        JSON.stringify({ page, pageParameter }),
      );
    } catch (_) {
      // A missing hint only makes the next lookup slower; access still works.
    }
  };

  const readCartOwner = () => {
    try {
      return window.localStorage.getItem(cartOwnerKey) || "";
    } catch (_) {
      return "";
    }
  };

  const writeCartOwner = (owner) => {
    try {
      if (owner) window.localStorage.setItem(cartOwnerKey, owner);
      else window.localStorage.removeItem(cartOwnerKey);
    } catch (_) {
      // The cart is still cleared explicitly when the agent changes.
    }
  };

  const parseDirectory = (documentRoot = document) => {
    const element = documentRoot.querySelector("#AstivaAgentDirectory");
    if (!element) return null;

    try {
      const payload = JSON.parse(element.textContent || "{}");
      const rawEntries = Array.isArray(payload) ? payload : payload.entries;
      const rawPagination =
        !Array.isArray(payload) && payload.pagination ? payload.pagination : {};
      const entries = (Array.isArray(rawEntries) ? rawEntries : [])
        .map((entry) => ({
          npn: normalizeNpn(entry.npn),
          agentId: String(entry.agentId || "").trim(),
          region: normalizeAgentRegion(entry.region),
        }))
        .filter((entry) => entry.npn && entry.region !== "none");

      const currentPage = Math.max(
        1,
        Number.parseInt(rawPagination.currentPage, 10) || 1,
      );
      const totalPages = Math.max(
        currentPage,
        Math.min(100, Number.parseInt(rawPagination.totalPages, 10) || 1),
      );
      const pageParameter = /^[a-zA-Z0-9_]+$/.test(rawPagination.pageParameter)
        ? rawPagination.pageParameter
        : "page";

      return { entries, currentPage, totalPages, pageParameter };
    } catch (error) {
      console.warn("[Astiva] The agent directory could not be parsed.", error);
      return {
        entries: [],
        currentPage: 1,
        totalPages: 1,
        pageParameter: "page",
      };
    }
  };

  const loadDirectoryPage = (pageNumber, pageParameter = "page") => {
    const page = Math.max(1, Number.parseInt(pageNumber, 10) || 1);
    if (directoryPagePromises.has(page)) return directoryPagePromises.get(page);

    if (inlineDirectoryPage === undefined)
      inlineDirectoryPage = parseDirectory(document);
    if (inlineDirectoryPage?.currentPage === page) {
      const inlinePromise = Promise.resolve(inlineDirectoryPage);
      directoryPagePromises.set(page, inlinePromise);
      return inlinePromise;
    }

    const directoryUrl = new URL(rootUrl);
    if (page > 1) directoryUrl.searchParams.set(pageParameter, String(page));

    const directoryPromise = window
      .fetch(directoryUrl.toString(), {
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      })
      .then((response) => {
        if (!response.ok)
          throw new Error(
            `Agent directory request failed with status ${response.status}`,
          );
        return response.text();
      })
      .then((html) => {
        const directoryPage = parseDirectory(
          new DOMParser().parseFromString(html, "text/html"),
        );
        if (!directoryPage)
          throw new Error("Agent directory was not present in the response");
        return directoryPage;
      })
      .catch((error) => {
        directoryPagePromises.delete(page);
        throw error;
      });

    directoryPagePromises.set(page, directoryPromise);
    return directoryPromise;
  };

  const findAgentByStorefrontApi = (npn) => {
    if (!storefrontApiEndpoint || !storefrontAccessToken) return null;
    if (directLookupPromises.has(npn)) return directLookupPromises.get(npn);

    const lookupPromise = window
      .fetch(storefrontApiEndpoint, {
        method: "POST",
        credentials: "omit",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
        },
        body: JSON.stringify({
          query: `
            query AstivaAgentByHandle($handle: MetaobjectHandleInput!) {
              metaobject(handle: $handle) {
                npn: field(key: "npn") { value }
                region: field(key: "region") { value }
                agentId: field(key: "agent_id") { value }
              }
            }
          `,
          variables: {
            handle: {
              type: agentMetaobjectType,
              handle: `npn-${npn}`,
            },
          },
        }),
      })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Storefront API lookup failed with status ${response.status}`,
          );
        }

        const payload = await response.json();
        if (payload.errors?.length) {
          throw new Error(
            payload.errors.map((error) => error.message).join("; "),
          );
        }

        const metaobject = payload.data?.metaobject;
        if (!metaobject) return null;

        const returnedNpn = normalizeNpn(metaobject.npn?.value);
        const region = normalizeAgentRegion(metaobject.region?.value);
        if (returnedNpn !== npn || region === "none") return null;

        return {
          npn: returnedNpn,
          agentId: String(metaobject.agentId?.value || "").trim(),
          region,
        };
      })
      .catch((error) => {
        directLookupPromises.delete(npn);
        throw error;
      });

    directLookupPromises.set(npn, lookupPromise);
    return lookupPromise;
  };

  const findAgentInDirectory = async (npn) => {
    const normalized = normalizeNpn(npn);
    if (!normalized) return null;
    let unresolvedLookupError = null;

    const findOnPage = (directoryPage) =>
      directoryPage.entries.find((entry) => entry.npn === normalized);
    const rememberedPage = readDirectoryHint();

    if (rememberedPage) {
      try {
        const directoryPage = await loadDirectoryPage(
          rememberedPage.page,
          rememberedPage.pageParameter,
        );
        const rememberedAgent = findOnPage(directoryPage);
        if (rememberedAgent) return rememberedAgent;
      } catch (_) {
        // Fall through to a complete lookup if the remembered page is stale.
      }
    }

    const firstPage = await loadDirectoryPage(1);
    let agent = findOnPage(firstPage);
    if (agent) {
      writeDirectoryHint(1, firstPage.pageParameter);
      return agent;
    }

    for (
      let firstPageNumber = 2;
      firstPageNumber <= firstPage.totalPages;
      firstPageNumber += directoryLookupConcurrency
    ) {
      const pageNumbers = Array.from(
        {
          length: Math.min(
            directoryLookupConcurrency,
            firstPage.totalPages - firstPageNumber + 1,
          ),
        },
        (_, index) => firstPageNumber + index,
      );
      const settledDirectoryPages = await Promise.allSettled(
        pageNumbers.map((page) =>
          loadDirectoryPage(page, firstPage.pageParameter),
        ),
      );

      for (const result of settledDirectoryPages) {
        if (result.status !== "fulfilled") continue;

        const directoryPage = result.value;
        agent = findOnPage(directoryPage);
        if (agent) {
          writeDirectoryHint(
            directoryPage.currentPage,
            firstPage.pageParameter,
          );
          return agent;
        }
      }

      for (let index = 0; index < settledDirectoryPages.length; index += 1) {
        if (settledDirectoryPages[index].status === "fulfilled") continue;

        try {
          const directoryPage = await loadDirectoryPage(
            pageNumbers[index],
            firstPage.pageParameter,
          );
          agent = findOnPage(directoryPage);
          if (agent) {
            writeDirectoryHint(
              directoryPage.currentPage,
              firstPage.pageParameter,
            );
            return agent;
          }
        } catch (error) {
          unresolvedLookupError = error;
        }
      }
    }

    if (unresolvedLookupError) throw unresolvedLookupError;
    return null;
  };

  const findAgent = async (npn) => {
    const normalized = normalizeNpn(npn);
    if (!normalized) return null;

    if (storefrontApiEndpoint && storefrontAccessToken) {
      try {
        return await findAgentByStorefrontApi(normalized);
      } catch (error) {
        console.warn(
          "[Astiva] Direct agent lookup failed; using the Liquid directory fallback.",
          error,
        );
      }
    }

    return findAgentInDirectory(normalized);
  };

  const cartUrl = (path) =>
    new URL(path.replace(/^\//, ""), rootUrl).toString();

  const getCart = async () => {
    const response = await window.fetch(cartUrl("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok)
      throw new Error(`Cart request failed with status ${response.status}`);
    return response.json();
  };

  const clearCart = async () => {
    const response = await window.fetch(cartUrl("cart/clear.js"), {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });

    if (!response.ok)
      throw new Error(`Cart reset failed with status ${response.status}`);

    try {
      window.sessionStorage.removeItem("cart-count");
    } catch (_) {
      // Ignore unavailable browser storage.
    }
  };

  const clearUnverifiedCart = async () => {
    const cart = await getCart();
    if ((Number.parseInt(cart.item_count, 10) || 0) < 1) return false;
    await clearCart();
    return true;
  };

  const updateCartAgent = async (agent) => {
    const response = await window.fetch(cartUrl("cart/update.js"), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({
        attributes: {
          "Astiva Agent NPN": agent.npn,
          "Astiva Agent ID": agent.agentId,
          "Astiva Agent Region": agent.region,
        },
      }),
    });

    if (!response.ok)
      throw new Error(
        `Cart agent update failed with status ${response.status}`,
      );
  };

  const syncCartOwner = async (agent) => {
    const nextOwner = `${agent.npn}:${agent.region}`;
    const previousOwner = readCartOwner();
    const changed = previousOwner !== nextOwner;

    if (changed) await clearCart();
    await updateCartAgent(agent);
    writeCartOwner(nextOwner);
    return changed;
  };

  const isCaliforniaAgent = (agent = currentAgent) =>
    agent?.region === "north-ca" || agent?.region === "south-ca";

  const checkoutLocationParameters = () => {
    const parameters = [[checkoutLocationParameterNames.country, "US"]];
    if (isCaliforniaAgent()) {
      parameters.push([checkoutLocationParameterNames.province, "CA"]);
    }
    return parameters;
  };

  const storefrontCartLine = (item) => {
    const line = {
      merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
      quantity: item.quantity,
    };
    const attributes = Object.entries(item.properties || {})
      .filter(([, value]) => value !== null && value !== "")
      .map(([key, value]) => ({ key, value: String(value) }));
    if (attributes.length) line.attributes = attributes;
    return line;
  };

  const createStorefrontCheckoutUrl = async (cart) => {
    if (!storefrontApiEndpoint || !storefrontAccessToken) return null;

    const lines = (Array.isArray(cart?.items) ? cart.items : [])
      .filter((item) => item.variant_id && item.quantity > 0)
      .map(storefrontCartLine);
    if (!lines.length) throw new Error("The cart has no checkout line items");

    const deliveryAddress = { countryCode: "US" };
    if (isCaliforniaAgent()) deliveryAddress.provinceCode = "CA";

    const input = {
      lines,
      buyerIdentity: { countryCode: "US" },
      delivery: {
        addresses: [
          {
            address: {
              deliveryAddress,
            },
            selected: true,
            oneTimeUse: true,
            validationStrategy: "COUNTRY_CODE_ONLY",
          },
        ],
      },
    };
    const attributes = Object.entries(cart.attributes || {})
      .filter(([, value]) => value !== null && value !== "")
      .map(([key, value]) => ({ key, value: String(value) }));
    if (attributes.length) input.attributes = attributes;
    if (cart.note) input.note = cart.note;

    const response = await window.fetch(storefrontApiEndpoint, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
      },
      body: JSON.stringify({
        query: `
          mutation AstivaCheckoutCartCreate($input: CartInput!, $language: LanguageCode!)
          @inContext(country: US, language: $language) {
            cartCreate(input: $input) {
              cart { checkoutUrl }
              userErrors { field message }
              warnings { message }
            }
          }
        `,
        variables: { input, language: storefrontLanguageCode },
      }),
    });
    if (!response.ok)
      throw new Error(
        `Storefront checkout cart creation failed with status ${response.status}`,
      );

    const payload = await response.json();
    const errors = [
      ...(payload.errors || []),
      ...(payload.data?.cartCreate?.userErrors || []),
    ];
    if (errors.length)
      throw new Error(errors.map((error) => error.message).join("; "));

    const checkoutUrl = payload.data?.cartCreate?.cart?.checkoutUrl;
    if (!checkoutUrl) throw new Error("Storefront checkout URL is missing");
    return checkoutUrl;
  };

  const createCheckoutPermalink = (cart) => {
    const lineItems = (Array.isArray(cart?.items) ? cart.items : [])
      .filter((item) => item.variant_id && item.quantity > 0)
      .map((item) => `${item.variant_id}:${item.quantity}`)
      .join(",");
    if (!lineItems) throw new Error("The cart has no checkout line items");

    const checkoutUrl = new URL(cartUrl(`cart/${lineItems}`));
    for (const [name, value] of checkoutLocationParameters()) {
      checkoutUrl.searchParams.set(name, value);
    }

    if (cart.note) checkoutUrl.searchParams.set("note", cart.note);
    for (const [name, value] of Object.entries(cart.attributes || {})) {
      if (value) checkoutUrl.searchParams.set(`attributes[${name}]`, value);
    }

    return checkoutUrl;
  };

  const redirectToCheckout = async () => {
    const cart = await getCart();
    try {
      const checkoutUrl = await createStorefrontCheckoutUrl(cart);
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
    } catch (error) {
      console.warn(
        "[Astiva] Storefront checkout creation failed; using the cart permalink fallback.",
        error,
      );
    }

    window.location.assign(createCheckoutPermalink(cart).toString());
  };

  const currentRouteRegion = () =>
    config.routeRegion || document.body.dataset.astivaRouteRegion || "none";

  const updateRegionalCollectionTitle = () => {
    const titles = {
      "north-ca": "Northern California",
      "south-ca": "Southern California",
    };
    const currentPath = normalizePathname(window.location.pathname);
    const region = Object.entries(regionalCollectionUrls).find(
      ([, collectionUrl]) => {
        if (!collectionUrl) return false;
        return (
          normalizePathname(new URL(collectionUrl, window.location.origin).pathname) ===
          currentPath
        );
      },
    )?.[0];
    if (!region) return;

    const heading = document.querySelector("#MainContent h1");
    if (heading) heading.textContent = titles[region];
  };

  updateRegionalCollectionTitle();

  const isProductAllowed = (productRegion, agentRegion) => {
    if (productRegion === "shared" || productRegion === "both") return true;
    if (enforceAgentRegion && agentRegion !== "other") {
      return productRegion === agentRegion;
    }

    const routeRegion = currentRouteRegion();
    return (
      routeRegion === "none" ||
      routeRegion === "shared" ||
      routeRegion === "both" ||
      productRegion === routeRegion
    );
  };

  const productContainer = (card) =>
    card.closest("li") ||
    card.closest("slideshow-slide") ||
    card.closest("[data-product-id]") ||
    card;

  const updateProductCounts = () => {
    for (const grid of document.querySelectorAll(
      '[data-testid="product-grid"]',
    )) {
      const items = Array.from(grid.children).filter(
        (item) => item instanceof HTMLElement,
      );
      const visibleCount = items.filter((item) => !item.hidden).length;
      const wrapper =
        grid.closest("results-list, collection-component, .shopify-section") ||
        document;

      for (const count of wrapper.querySelectorAll(
        '[data-testid="products-count"] span',
      )) {
        const template =
          visibleCount === 1 ? config.copy?.itemOne : config.copy?.itemOther;
        const nextText = String(template || `${visibleCount} items`).replace(
          /\d+/,
          String(visibleCount),
        );
        if (count.textContent !== nextText) count.textContent = nextText;
      }

      for (const facets of wrapper.querySelectorAll("facets-form-component")) {
        facets.dataset.resultsCount = String(visibleCount);
      }
    }
  };

  const applyFeaturedProductLayout = () => {
    for (const grid of document.querySelectorAll(
      '[data-testid="product-grid"]',
    )) {
      const items = Array.from(grid.children).filter(
        (item) => item instanceof HTMLElement,
      );
      for (const item of items) {
        delete item.dataset.astivaFeaturedActive;
        item.style.removeProperty("grid-column-start");
        item.style.removeProperty("order");
      }
      delete grid.dataset.astivaFeaturedCount;

      if (
        grid.dataset.astivaFeaturedLayout !== "true" ||
        grid.getAttribute("product-grid-view") === "zoom-out"
      )
        continue;

      const columnCount = Math.max(
        1,
        window
          .getComputedStyle(grid)
          .gridTemplateColumns.split(/\s+/)
          .filter(Boolean).length,
      );

      const featuredItems = items
        .filter(
          (item) =>
            !item.hidden && item.dataset.astivaFeaturedProduct === "true",
        )
        .sort(
          (first, second) =>
            (Number.parseInt(first.dataset.astivaFeaturedPriority, 10) || 3) -
            (Number.parseInt(second.dataset.astivaFeaturedPriority, 10) || 3),
        )
        .slice(0, 2);

      if (!featuredItems.length) continue;
      grid.dataset.astivaFeaturedCount = String(featuredItems.length);
      for (const [index, item] of featuredItems.entries()) {
        item.dataset.astivaFeaturedActive = "true";
        item.style.order = String(index - featuredItems.length);
        item.style.gridColumnStart = "1";
      }

      const standardItems = items.filter(
        (item) => !item.hidden && !featuredItems.includes(item),
      );
      for (const [index, item] of standardItems.entries()) {
        item.style.gridColumnStart = String((index % columnCount) + 1);
      }
    }
  };

  const applyProductVisibility = () => {
    if (!currentAgent) return;

    const containers = new Map();
    for (const card of document.querySelectorAll(
      "[data-astiva-product-region]",
    )) {
      const container = productContainer(card);
      const allowed = isProductAllowed(
        card.dataset.astivaProductRegion,
        currentAgent.region,
      );
      containers.set(
        container,
        (containers.get(container) ?? false) || allowed,
      );
    }

    for (const [container, allowed] of containers) {
      container.hidden = !allowed;
    }

    updateProductCounts();
    applyFeaturedProductLayout();
  };

  const updateAccessUi = () => {
    const authorized = Boolean(currentAgent);
    document.body.dataset.agentAuthorized = String(authorized);
    document.body.dataset.agentRegion = currentAgent?.region || "none";
    document.body.dataset.agentUnlimitedQuantity = String(
      authorized && currentAgent.npn === unlimitedQuantityNpn,
    );
    document.body.classList.remove(
      "agent-region-none",
      "agent-region-north-ca",
      "agent-region-south-ca",
      "agent-region-other",
    );
    document.body.classList.add(
      `agent-region-${currentAgent?.region || "none"}`,
    );

    for (const trigger of document.querySelectorAll(
      "[data-astiva-agent-access-trigger]",
    )) {
      const label = trigger.querySelector("[data-astiva-agent-access-label]");
      trigger.dataset.verified = String(authorized);
      trigger.setAttribute(
        "aria-label",
        authorized
          ? `${config.copy?.buttonVerified}: ${currentAgent.npn}`
          : config.copy?.button,
      );
      trigger.title = authorized
        ? `NPN ${currentAgent.npn}`
        : config.copy?.button || "";
      if (label)
        label.textContent = authorized
          ? config.copy?.buttonVerified
          : config.copy?.button;
    }

    if (verifiedPanel) verifiedPanel.hidden = !authorized;
    if (verifiedMessage) {
      verifiedMessage.textContent = authorized ? `NPN ${currentAgent.npn}` : "";
    }
    if (input) input.value = currentAgent?.npn || "";

    if (authorized) applyProductVisibility();
    document.dispatchEvent(
      new CustomEvent("astiva:agent-access-change", {
        detail: currentAgent || null,
      }),
    );
  };

  const setStatus = (message, state = "error") => {
    if (!status) return;
    status.textContent = message || "";
    if (message) status.dataset.state = state;
    else delete status.dataset.state;
  };

  const openDialog = (destination) => {
    pendingDestination = destination || pendingDestination;
    setStatus("");
    updateAccessUi();

    if (typeof dialog?.showModal === "function") {
      if (!dialog.open) dialog.showModal();
      window.setTimeout(() => input?.focus(), 0);
    }
  };

  const closeDialog = () => {
    if (dialog?.open) dialog.close();
  };

  const safeReturnDestination = () => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("return_to");
    if (!value) return "";

    try {
      const destination = new URL(value, window.location.origin);
      return destination.origin === window.location.origin
        ? destination.href
        : "";
    } catch (_) {
      return "";
    }
  };

  const redirectToAccess = () => {
    const destination = new URL(rootUrl);
    destination.searchParams.set("agent_access", "required");
    destination.searchParams.set(
      "return_to",
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
    window.location.replace(destination.toString());
  };

  const routeMatchesRegion = (agent) => {
    if (!enforceAgentRegion) return true;
    if (agent.region === "other") return true;

    const routeRegion = currentRouteRegion();
    return (
      routeRegion === "none" ||
      routeRegion === "shared" ||
      routeRegion === "both" ||
      routeRegion === agent.region
    );
  };

  const revealPage = () =>
    document.documentElement.classList.remove("astiva-access-checking");

  const verifyStoredAccess = async () => {
    if (!config.enabled || config.designMode) {
      revealPage();
      return;
    }

    const storedNpn = readSessionNpn();
    if (storedNpn) {
      try {
        currentAgent = await findAgent(storedNpn);
      } catch (error) {
        console.warn("[Astiva] Agent access could not be verified.", error);
        currentAgent = null;
      }
    }

    let unverifiedCartWasCleared = false;
    if (!currentAgent) {
      writeSessionNpn("");
      writeCartOwner("");

      try {
        unverifiedCartWasCleared = await clearUnverifiedCart();
      } catch (error) {
        console.warn("[Astiva] The unverified cart could not be cleared.", error);
      }
    }

    if (currentAgent && !routeMatchesRegion(currentAgent)) {
      window.location.replace(rootUrl.toString());
      return;
    }

    if (currentAgent && isCatalogUrl(new URL(window.location.href))) {
      const destination = regionalCollectionUrl(currentAgent);
      if (destination) {
        window.location.replace(destination.toString());
        return;
      }
    }

    if (!currentAgent && config.accessRequired) {
      redirectToAccess();
      return;
    }

    if (unverifiedCartWasCleared) {
      window.location.reload();
      return;
    }

    updateAccessUi();
    revealPage();

    if (currentAgent) {
      try {
        const cartChanged = await syncCartOwner(currentAgent);
        if (cartChanged) {
          window.location.reload();
          return;
        }
      } catch (error) {
        console.warn(
          "[Astiva] The cart could not be synchronized with the agent.",
          error,
        );
      }
    }

    const params = new URLSearchParams(window.location.search);
    if (!currentAgent && params.get("agent_access") === "required") {
      pendingDestination = safeReturnDestination();
      openDialog(pendingDestination);
    }
  };

  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const npn = normalizeNpn(input?.value);
    if (!npn) {
      setStatus(config.copy?.required || "Enter your NPN to continue.");
      input?.focus();
      return;
    }

    isSubmitting = true;
    if (submitButton) submitButton.disabled = true;
    setStatus(config.copy?.checking || "Checking NPN...", "success");

    try {
      const agent = await findAgent(npn);
      if (!agent) {
        setStatus(
          config.copy?.invalid || "This NPN is not on the approved agent list.",
        );
        input?.select();
        return;
      }

      const previousNpn = readSessionNpn();
      writeSessionNpn(agent.npn);
      currentAgent = agent;
      updateAccessUi();

      const ownerChanged = await syncCartOwner(agent);
      const cartChanged = previousNpn !== agent.npn || ownerChanged;
      const destination = resolveAgentDestination(
        pendingDestination || safeReturnDestination(),
        agent,
      );
      pendingDestination = "";
      closeDialog();

      if (destination) {
        window.location.assign(destination);
      } else if (cartChanged) {
        window.location.reload();
      }
    } catch (error) {
      console.warn("[Astiva] NPN verification failed.", error);
      setStatus(
        config.copy?.error || "We could not verify your NPN. Please try again.",
      );
    } finally {
      isSubmitting = false;
      if (submitButton) submitButton.disabled = false;
    }
  });

  input?.addEventListener("input", () => setStatus(""));

  clearButton?.addEventListener("click", async () => {
    clearButton.disabled = true;
    try {
      await clearCart();
    } catch (error) {
      console.warn(
        "[Astiva] The cart could not be cleared when agent access was removed.",
        error,
      );
    }

    writeSessionNpn("");
    writeCartOwner("");
    currentAgent = null;
    closeDialog();
    window.location.assign(rootUrl.toString());
  });

  dialog?.addEventListener("click", (event) => {
    if (event.target === dialog) closeDialog();
  });

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const accessTrigger = target.closest(
        "[data-astiva-agent-access-trigger]",
      );
      if (accessTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDialog();
        return;
      }

      const checkoutTrigger = target.closest('[name="checkout"]');
      if (
        checkoutTrigger &&
        config.enabled &&
        !config.designMode &&
        !currentAgent
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDialog(new URL("cart", rootUrl).toString());
        return;
      }

      const link = target.closest("a[href]");
      let linkDestination = null;
      if (link && !link.hasAttribute("download") && link.target !== "_blank") {
        try {
          linkDestination = new URL(link.href, window.location.href);
        } catch (_) {
          linkDestination = null;
        }
      }

      if (currentAgent && linkDestination && isCatalogUrl(linkDestination)) {
        const destination = regionalCollectionUrl(currentAgent);
        if (destination) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.location.assign(destination.toString());
        }
        return;
      }

      if (currentAgent || !config.enabled || config.designMode) return;

      const cartTrigger = target.closest('[data-testid="cart-drawer-trigger"]');
      if (cartTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDialog(new URL("cart", rootUrl).toString());
        return;
      }

      if (!linkDestination) return;
      const destination = linkDestination;

      if (destination.origin !== window.location.origin) return;
      if (
        destination.pathname === rootUrl.pathname ||
        destination.pathname.startsWith("/localization")
      )
        return;
      if (
        destination.pathname.startsWith("/cdn/") ||
        destination.pathname.startsWith("/apps/")
      )
        return;

      event.preventDefault();
      event.stopImmediatePropagation();
      openDialog(destination.href);
    },
    true,
  );

  document.addEventListener(
    "submit",
    async (event) => {
      const cartForm =
        event.target instanceof HTMLFormElement ? event.target : null;
      if (
        !cartForm?.matches(".cart-form") ||
        event.submitter?.name !== "checkout" ||
        !config.enabled ||
        config.designMode
      )
        return;

      if (!currentAgent) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openDialog(new URL("cart", rootUrl).toString());
        return;
      }

      if (bypassCheckoutPermalink) {
        bypassCheckoutPermalink = false;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      if (isPreparingCheckout) return;

      const checkoutButton = event.submitter;
      isPreparingCheckout = true;
      if (checkoutButton instanceof HTMLButtonElement) checkoutButton.disabled = true;

      try {
        await redirectToCheckout();
      } catch (error) {
        console.warn("[Astiva] Checkout location could not be prefilled.", error);
        isPreparingCheckout = false;
        if (checkoutButton instanceof HTMLButtonElement) checkoutButton.disabled = false;
        bypassCheckoutPermalink = true;
        if (checkoutButton instanceof HTMLElement) cartForm.requestSubmit(checkoutButton);
        else cartForm.requestSubmit();
      }
    },
    true,
  );

  let visibilityQueued = false;
  const queueProductVisibility = () => {
    if (!currentAgent || visibilityQueued) return;
    visibilityQueued = true;
    window.requestAnimationFrame(() => {
      visibilityQueued = false;
      applyProductVisibility();
    });
  };
  const observer = new MutationObserver(queueProductVisibility);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["product-grid-view"],
  });
  window.addEventListener("resize", queueProductVisibility, { passive: true });

  verifyStoredAccess();
})();
